import React, { useState, useCallback, useEffect } from 'react';
import { Card, Button, Form, InputGroup, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../hooks/useRedux';
import { submitInvite, clearInviteState } from '../features/invite/invite.slice';
import { setSelectedFreelancersForInvoice } from '../features/customer/customer.slice';
import { validateFreelancerIdentity } from '../features/invoice/invoice.slice';
import { notify } from '../helper/notify';
import './collective-invoice.scss';

interface Friend {
  id: string;
  userId?: string | number; // Add userId field
  name: string;
  email: string;
}

interface CollectiveInvoiceProps {
  onCollectiveInvoiceClick?: () => void;
  copiedInvoiceData?: any;  // NEW - Optional copied invoice data
  isEditModeInvoice?: boolean;  // NEW - Edit mode flag
  stepLabel?: string;    // NEW - e.g. "Step 2 of 6"
  stepTitle?: string;    // NEW - e.g. "Collaborators"
  stepSubtitle?: string; // NEW - e.g. "Add freelancers"
}

const CollectiveInvoice: React.FC<CollectiveInvoiceProps> = ({ onCollectiveInvoiceClick, copiedInvoiceData, isEditModeInvoice, stepLabel, stepTitle, stepSubtitle }) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [invitedFriends, setInvitedFriends] = useState<string[]>([]);
  const [showTooltip, setShowTooltip] = useState(false);
  const searchWrapperRef = React.useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const customerState = useAppSelector((state: any) => state?.customer || {});
  const { inviteData, loading: inviteLoading, error } = useAppSelector((state) => state?.invite || { inviteData: null, loading: false, error: null });
  const { customers = [], loading: customerLoading = false, error: customerError = null, selectedCustomer, freelancers = [] } = customerState;

  const [friends, setFriends] = useState<Friend[]>([]);
  const [selectedFreelancerIds, setSelectedFreelancerIds] = useState<Set<string>>(new Set());
  const [showSearchError, setShowSearchError] = useState(false);
  // Check if customer is selected
  const isCustomerSelected = !!selectedCustomer;

  // Handle copiedInvoiceData and auto-select freelancers
  useEffect(() => {
    if (copiedInvoiceData && copiedInvoiceData.invoiceFreelancer) {

      const invoiceFreelancers = copiedInvoiceData.invoiceFreelancer;

      if (Array.isArray(invoiceFreelancers) && invoiceFreelancers.length > 0) {
        // Skip index 0, auto-select rest of the freelancers
        const freelancersToSelect = invoiceFreelancers.slice(1); // Skip first one (index 0)
        const selectedFriends: Friend[] = [];

        freelancersToSelect.forEach((invoiceFreelancer: any) => {
          const freelancerDetails = invoiceFreelancer.freelancerDetails;

          if (freelancerDetails) {
            // Check if this freelancer exists in the available freelancers list
            const matchingFreelancer = freelancers.find((f: any) =>
              f.signUpUser?.userId === freelancerDetails.userId ||
              f.signUpUser?.email === freelancerDetails.email ||
              f.userId === freelancerDetails.userId
            );

            if (matchingFreelancer) {
              const signUpUser = matchingFreelancer.signUpUser || {};
              const friend: Friend = {
                id: matchingFreelancer.id?.toString() || invoiceFreelancer.userId?.toString() || Date.now().toString(),
                userId: invoiceFreelancer.userId,
                name: `${freelancerDetails.firstName || ''} ${freelancerDetails.lastName || ''}`.trim() || freelancerDetails.email || 'Unknown User',
                email: freelancerDetails.email || ''
              };

              // Check if not already added
              if (!friends.some(f => f.email === friend.email)) {
                selectedFriends.push(friend);  }
            }
          }
        });

        if (selectedFriends.length > 0) {
          setFriends(prevFriends => [...prevFriends, ...selectedFriends]);
         }
      }
    }
  }, [copiedInvoiceData, freelancers]);

  // Auto-select freelancer at index 0 when customer is selected and freelancers are available
  useEffect(() => {
    if (isCustomerSelected && freelancers.length > 0) {
      const firstFreelancer = freelancers[0];
      const signUpUser = firstFreelancer.signUpUser || {};
      const firstFriend: Friend = {
        id: firstFreelancer.id?.toString() || '0',
        userId: firstFreelancer.userId || signUpUser.userId || signUpUser.id, // Add userId
        name: `${signUpUser.firstName || ''} ${signUpUser.lastName || ''}`.trim() || signUpUser.email || 'Unknown User',
        email: signUpUser.email || ''
      };
      // Only add if this person isn't already in friends. Match by userId/email
      // (NOT just id) — the redux→friends sync can produce a differently-id'd
      // copy of "you", and an id-only check would then add a duplicate.
      const alreadyAdded = friends.some(f =>
        (firstFriend.userId != null && f.userId === firstFriend.userId) ||
        (!!firstFriend.email && f.email === firstFriend.email) ||
        f.id === firstFriend.id
      );
      if (!alreadyAdded) {
        setFriends(prevFriends => [firstFriend, ...prevFriends]);
      }
    }
  }, [isCustomerSelected, freelancers]);

  // Sync friends to Redux whenever they change
  useEffect(() => {
    dispatch(setSelectedFreelancersForInvoice(friends));
  }, [friends, dispatch]);

  // Sync from Redux back to friends when selectedFreelancersForInvoice changes externally
  // (e.g., when a freelancer is removed from item-details component)
  useEffect(() => {
    const selectedFreelancers = customerState.selectedFreelancersForInvoice || [];

    // De-duplicate by person (userId → email → id) so a stale/duplicate entry in
    // the store can't show a wrong count ("2 freelancers" while only 1 is listed).
    const seen = new Set<string>();
    const dedupedFreelancers: any[] = selectedFreelancers.filter((f: any) => {
      const key = (f?.userId ?? f?.email ?? f?.id)?.toString() || '';
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Only sync if there's a meaningful difference
    if (dedupedFreelancers.length !== friends.length) {
      // Convert selectedFreelancersForInvoice to friends format
      const updatedFriends: Friend[] = dedupedFreelancers.map((freelancer: any) => ({
        id: freelancer.id?.toString() || freelancer.userId?.toString() || '',
        userId: freelancer.userId || freelancer.id,
        name: freelancer.name || freelancer.userName || `${freelancer.firstName || ''} ${freelancer.lastName || ''}`.trim() || freelancer.email || 'Unknown User',
        email: freelancer.email || ''
      }));

      setFriends(updatedFriends);
    }
  }, [customerState.selectedFreelancersForInvoice]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter freelancers based on search value
  const filteredFreelancers = freelancers.filter((freelancer: any) => {
    if (!searchValue.trim()) return false;
    const searchTerm = searchValue.toLowerCase();
    const signUpUser = freelancer.signUpUser || {};
    return (
      signUpUser.firstName?.toLowerCase().includes(searchTerm) ||
      signUpUser.lastName?.toLowerCase().includes(searchTerm) ||
      signUpUser.email?.toLowerCase().includes(searchTerm) ||
      `${signUpUser.firstName || ''} ${signUpUser.lastName || ''}`.toLowerCase().includes(searchTerm)
    );
  });

  // Show suggestions when focused and no search value, or when there are filtered results
  const shouldShowSuggestions = showSuggestions && (
    (searchValue.trim() === '' && freelancers.length > 0) ||
    (searchValue.trim() !== '' && filteredFreelancers.length > 0)
  );

  // Get suggestions to display - filter out already added friends AND the freelancer at index 0
  const availableFreelancers = freelancers.filter((freelancer: any, index: number) => {
    // Hide freelancer at index 0
    if (index === 0) return false;

    const freelancerEmail = freelancer.signUpUser?.email;
    return !friends.some(friend => friend.email === freelancerEmail);
  });

  const availableFilteredFreelancers = filteredFreelancers.filter((freelancer: any) => {
    // Hide freelancer at index 0
    if (freelancers.length > 0 && freelancer.id === freelancers[0].id) return false;

    const freelancerEmail = freelancer.signUpUser?.email;
    return !friends.some(friend => friend.email === freelancerEmail);
  });

  const suggestionsToShow = searchValue.trim() === ''
    ? availableFreelancers // Show all available freelancers when no search
    : availableFilteredFreelancers; // Show filtered available results when searching

  const handleCardClick = () => {
    setIsExpanded(true);
    if (onCollectiveInvoiceClick) {
      onCollectiveInvoiceClick();
    }
  };

  const handleCollapseClick = () => {
    setIsExpanded(false);
  };

  // Verify a freelancer's identity/SSN before they are added to the invoice.
  // Returns true when verified (200); on a 400 the backend message is surfaced
  // via notify and false is returned so the caller can block the add.
  const verifyFreelancerIdentity = async (friend: Friend): Promise<boolean> => {
    const userId = Number(friend.userId);
    // If we can't resolve a backend userId, don't block here — the create-invoice
    // submit still enforces identity validation server-side.
    if (!userId) {
      return true;
    }
    try {
      await dispatch(validateFreelancerIdentity(userId)).unwrap();
      return true;
    } catch (message) {
      const raw = String(message ?? '');
      // The backend returns this identity error in English with the freelancer's
      // name interpolated (so it never matches a translation key). Detect it and
      // show a localized message with the name; otherwise fall back to the raw
      // message (still passed through t() in case it's a known key).
      if (/identity verification/i.test(raw)) {
        notify.error(t('FREELANCER_IDENTITY_NOT_VERIFIED', { name: friend.name }) as string);
      } else {
        notify.error((t(raw) as string) || raw);
      }
      return false;
    }
  };

  const handleAddFriend = async () => {
     // Check if no freelancers are selected
    if (selectedFreelancerIds.size === 0) {
      // Show error message
      setShowSearchError(true);
      return;
    }

    // Clear any error message
    setShowSearchError(false);

    // Collect the selected freelancers that are not already added
    const candidates: Friend[] = [];
    selectedFreelancerIds.forEach(freelancerId => {
      const freelancer = freelancers.find((f: any) => f.id?.toString() === freelancerId);
      if (freelancer) {
        const signUpUser = freelancer.signUpUser || {};
        const newFriend: Friend = {
          id: freelancer.id?.toString() || Date.now().toString(),
          userId: freelancer.userId || signUpUser.userId || signUpUser.id, // Add userId
          name: `${signUpUser.firstName || ''} ${signUpUser.lastName || ''}`.trim() || signUpUser.email || 'Unknown User',
          email: signUpUser.email || ''
        };
        // Check if friend doesn't already exist
        if (!friends.some(f => f.email === newFriend.email)) {
          candidates.push(newFriend);
        }
      }
    });

    // Validate identity on the spot — add only the verified freelancers.
    // Each unverified freelancer surfaces its own message via notify.error.
    const verifiedFriends: Friend[] = [];
    for (const candidate of candidates) {
      const isVerified = await verifyFreelancerIdentity(candidate);
      if (isVerified) {
        verifiedFriends.push(candidate);
      }
    }

    if (verifiedFriends.length > 0) {
      setFriends([...friends, ...verifiedFriends]);
    }
    // Reset the selection/search regardless — unverified freelancers are simply
    // left out and never reach the invoice/Redux.
    setSelectedFreelancerIds(new Set());
    setSearchValue('');
    setShowSuggestions(false);
    // Do not allow adding if no freelancer is selected from dropdown
    // User must select from the available freelancer list only
  };

  const handleAddFreelancer = async (freelancer: any) => {
    const signUpUser = freelancer.signUpUser || {};
    const newFriend: Friend = {
      id: freelancer.id?.toString() || Date.now().toString(),
      userId: freelancer.userId || signUpUser.userId || signUpUser.id, // Add userId
      name: `${signUpUser.firstName || ''} ${signUpUser.lastName || ''}`.trim() || signUpUser.email || 'Unknown User',
      email: signUpUser.email || ''
    };
    // Check if friend already exists
    if (!friends.some(friend => friend.email === newFriend.email)) {
      // Validate identity on the spot — block the add if not verified.
      const isVerified = await verifyFreelancerIdentity(newFriend);
      if (!isVerified) {
        setSearchValue('');
        setShowSuggestions(false);
        return;
      }
      setFriends([...friends, newFriend]);
    }
    setSearchValue('');
    setShowSuggestions(false);
  };

  const handleSearchFocus = () => {
    setShowSuggestions(true);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setShowSearchError(false); // Clear error when user types
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (freelancer: any, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    handleAddFreelancer(freelancer);
  };

  const handleFreelancerCheckboxChange = (freelancerId: string, event?: React.ChangeEvent<HTMLInputElement>) => {
    if (event) {
      event.stopPropagation();
    }
    const newSelected = new Set(selectedFreelancerIds);
    if (newSelected.has(freelancerId)) {
      newSelected.delete(freelancerId);
    } else {
      newSelected.add(freelancerId);
    }
    setSelectedFreelancerIds(newSelected);
  };

  const handleSelectAll = () => {
    const allIds = new Set(suggestionsToShow.map((f: any) => f.id?.toString()).filter(Boolean) as string[]);
    if (selectedFreelancerIds.size === allIds.size) {
      setSelectedFreelancerIds(new Set());
    } else {
      setSelectedFreelancerIds(allIds);
    }
  };

  const handleRemoveFriend = (friendId: string) => {
    // Prevent removal of the freelancer at index 0
    if (freelancers.length > 0 && friendId === freelancers[0].id?.toString()) {
      return;
    }
    setFriends(friends.filter(f => f.id !== friendId));
  };

  const handleInviteFriend = () => {
    setShowInviteModal(true);
  };

  const handleCloseInviteModal = () => {
    setShowInviteModal(false);
    setInviteEmail('');
  };

  // Email validation function
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  const handleAddInvite = () => {
    if (inviteEmail.trim() && isValidEmail(inviteEmail) && !invitedFriends.includes(inviteEmail.trim())) {
      setInvitedFriends([...invitedFriends, inviteEmail.trim()]);
      setInviteEmail('');
    }
  };

  const handleRemoveInvite = (email: string) => {
    setInvitedFriends(invitedFriends.filter(f => f !== email));
    // Clear invite data from Redux state when removing emails
    dispatch(clearInviteState());
  };

  const handleSubmitInvites = useCallback(async () => {
    if (invitedFriends.length === 0) {
      toast.error('Please add at least one email to invite');
      return;
    }

    try {
      const result = await dispatch(submitInvite(invitedFriends));

      if (submitInvite.fulfilled.match(result)) {
        toast.success('Invitations sent successfully!');
        setInvitedFriends([]);
        handleCloseInviteModal();
      } else {
        // Show the specific error message from the API
        const errorMessage = result.error?.message || 'Failed to send invitations. Please try again.';
        toast.error(errorMessage);
      }
    } catch (error) {
      console.error('Error sending invitations:', error);
      toast.error('Failed to send invitations. Please try again.');
    }
  }, [dispatch, invitedFriends]);

  // Freelancers already added (excluding the locked index-0 current user) shown as chips
  const collaboratorChips = friends.filter((friend) => {
    if (freelancers.length > 0) {
      const firstFreelancer = freelancers[0];
      const firstFreelancerUserId = firstFreelancer.userId || firstFreelancer.signUpUser?.userId || firstFreelancer.signUpUser?.id;
      const firstFreelancerEmail = firstFreelancer.signUpUser?.email;
      return !(friend.userId === firstFreelancerUserId || friend.email === firstFreelancerEmail);
    }
    return true;
  });

  const currentUserFreelancer = freelancers.length > 0 ? (freelancers[0].signUpUser || {}) : null;
  const currentUserName = currentUserFreelancer
    ? (`${currentUserFreelancer.firstName || ''} ${currentUserFreelancer.lastName || ''}`.trim() || currentUserFreelancer.email || (t('YOU') as string))
    : '';

  return (
    <>
      <div className="collab-step">
        <div className="invoice-card collab-outer">
        {/* Step header */}
        {(stepTitle || stepLabel) && (
          <div className="cust-step-header">
            <div className="cust-step-heading iw-head-with-icon">
              <span className="iw-head-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></svg>
              </span>
              <div className="iw-head-text">
                {stepLabel && (
                  <span className="cust-step-pill"><span className="cust-step-dot" />{stepLabel}</span>
                )}
                {stepTitle && <h2 className="cust-step-title">{stepTitle}</h2>}
                {stepSubtitle && <p className="cust-step-sub">{stepSubtitle}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Create a collective invoice */}
        <div className="collab-main-card">
            <div className="collab-head">
              <div className="collab-head-icon" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div className="collab-head-text">
                <h3>{t('COLLECTIVE_INVOICE_LABEL')}</h3>
                <p>{t('COLLAB_CARD_DESC')}</p>
              </div>
            </div>

            {/* Search + Add */}
            <div
              ref={searchWrapperRef}
              className="collab-search-row"
              onMouseEnter={() => !isCustomerSelected && setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <div className="collab-search-input-wrap">
                <svg className="collab-search-icon" width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                  <circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2" />
                  <path d="m19 19-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <Form.Control
                  type="text"
                  placeholder={t('SEARCH_FREELANCERS_BY_EMAIL_NAME')}
                  value={searchValue}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  className="search-input collab-search-input"
                  disabled={!isCustomerSelected || isEditModeInvoice}
                />
              </div>

              {showSearchError && (
                <div className="collab-inline-error">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M8 5.33333V8M8 10.6667H8.00667M14.6667 8C14.6667 11.6819 11.6819 14.6667 8 14.6667C4.3181 14.6667 1.33333 11.6819 1.33333 8C1.33333 4.3181 4.3181 1.33333 8 1.33333C11.6819 1.33333 14.6667 4.3181 14.6667 8Z" stroke="#DC2626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  {t('INVITE_FREELANCER_FIRST_ERROR')}
                </div>
              )}
              {showTooltip && !isCustomerSelected && (
                <div className="collab-tooltip">{t('PLEASE_SELECT_CUSTOMER_FIRST')}</div>
              )}

              {/* Searchable dropdown — click a freelancer to add them directly */}
              {showSuggestions && isCustomerSelected && (
                <div className="collab-suggestions collab-suggestions--dropdown">
                  <div className="collab-suggestions-list">
                    {suggestionsToShow.length > 0 ? (
                      suggestionsToShow.map((freelancer: any) => {
                        const fName = `${freelancer.signUpUser?.firstName || ''} ${freelancer.signUpUser?.lastName || ''}`.trim() || freelancer.signUpUser?.email || 'Unknown User';
                        return (
                          <button
                            key={freelancer.id}
                            type="button"
                            className="collab-suggestion-item"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => handleSuggestionClick(freelancer, e)}
                          >
                            <span className="collab-suggestion-avatar">{(fName || '?').charAt(0).toUpperCase()}</span>
                            <div className="freelancer-info">
                              <div className="freelancer-name">{fName}</div>
                              <div className="freelancer-email">{freelancer.signUpUser?.email || 'No email'}</div>
                            </div>
                            <span className="collab-suggestion-add" aria-hidden="true">
                              <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M8 3.33v9.34M3.33 8h9.34" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                            </span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="collab-suggestions-empty">
                        {searchValue.trim() ? t('NO_FREELANCERS_FOUND', { searchValue }) : t('NO_FREELANCERS_AVAILABLE')}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Collaborators */}
            <div className="collab-collaborators">
              <span className="collab-section-label">{t('COLLABORATORS')}</span>
              <div className="friends-section">
                {/* Current user — locked default chip (cannot be removed) */}
                {currentUserFreelancer && (
                  <div className="friend-tag friend-tag--locked" title={t('CURRENT_USER_LOCKED') as string}>
                    <span className="collab-chip-avatar">{(currentUserName || '?').charAt(0).toUpperCase()}</span>
                    <span className="friend-name">{currentUserName}</span>
                    <span className="you-badge">{t('YOU')}</span>
                  </div>
                )}
                {collaboratorChips.map((friend) => (
                  <div key={friend.id} className="friend-tag">
                    <span className="collab-chip-avatar">{(friend.name || '?').charAt(0).toUpperCase()}</span>
                    <span className="friend-name">{friend.name}</span>
                    <button
                      className="remove-friend-btn"
                      onClick={() => handleRemoveFriend(friend.id)}
                      disabled={isEditModeInvoice}
                      aria-label="Remove collaborator"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" /></svg>
                    </button>
                  </div>
                ))}
              </div>
            </div>
        </div>

            {/* Invite freelancers — merged into the same Collaborators card */}
            <div className="collab-invite-card">
            <div className="collab-invite-row">
              <div className="collab-invite-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                  <path d="M20 12v8H4v-8M22 7H2v5h20V7zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="collab-invite-text">
                <h4>{t('INVITE_CARD_TITLE')}</h4>
                <p>{t('INVITE_LOYALTY_DESC')}</p>
              </div>
              <Button
                className="invite-friend-btn collab-invite-btn"
                onClick={handleInviteFriend}
                disabled={isEditModeInvoice}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M12 2l2.9 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l7.1-1.01L12 2z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>
                {t('INVITE_FREELANCERS')}
              </Button>
            </div>
        </div>
        </div>
      </div>

      {/* Invite Friends Modal */}
      <Modal show={showInviteModal} onHide={handleCloseInviteModal} centered className="invite-friends-modal">
        <Modal.Header className="invite-modal-header">
          <Modal.Title className="invite-modal-title">{t('INVITE_FREELANCERS')}</Modal.Title>
          <button className="invite-modal-close" onClick={handleCloseInviteModal}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </Modal.Header>

        <Modal.Body className="invite-modal-body">
          <p className="invite-modal-description">
            {t('INVITE_FREELANCERS_WORKING_WITH_YOU')}
          </p>

          {/* Invited Friends List */}
          {invitedFriends.map((email, index) => (
            <div key={index} className="invited-friend-item">
              <span className="invited-friend-email">{email}</span>
              <button className="remove-invite-btn" onClick={() => handleRemoveInvite(email)}>
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M16 12H8M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="#E33B32" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          ))}

          {/* Email Input */}
          <div className="invite-input-section">
            <label className="invite-input-label">{t('INVITE_FREELANCER')}</label>
            <div className="invite-input-wrapper">
              <Form.Control
                type="email"
                placeholder={t('ENTER_FREELANCER_EMAIL')}
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className="invite-email-input"
              />
              <svg className="email-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="currentColor" strokeWidth="2" />
                <polyline points="22,6 12,13 2,6" stroke="currentColor" strokeWidth="2" />
              </svg>
              <Button
                className="invite-add-btn"
                onClick={handleAddInvite}
                disabled={!inviteEmail.trim() || !isValidEmail(inviteEmail)}
              >
                {t('ADD')}
              </Button>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer className="invite-modal-footer">
          <Button variant="outline-primary" className="invite-cancel-btn" onClick={handleCloseInviteModal} disabled={inviteLoading}>
            {t('CANCEL')}
          </Button>
          <Button variant="primary" className="invite-submit-btn" onClick={handleSubmitInvites} disabled={inviteLoading}>
            {inviteLoading ? t('SENDING') : t('SUBMIT')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default CollectiveInvoice;
